import { useState, useEffect, useCallback } from "react";
import { analyticsApi, ApiError } from "@/services/apiClient";
import { fmt } from "@/lib/format";
import ToggleGroup from "@/components/ToggleGroup";
import SectionRangePicker from "@/components/SectionRangePicker";
import CopyButton from "@/components/CopyButton";
import ExportButton from "@/components/ExportButton";
import MiniSelect from "@/components/MiniSelect";
import { cachedCall } from "@/lib/queryCache";

// 충전/환불/수기지급 내역 — "들어온 돈 상세"(2026-07-18). 매출·마진 탭의 "충전 내역"
// 세그먼트 본문. 예전엔 접힘 드릴다운이었는데 펼치면 스크롤이 무한정 길어져서(2026-07-18
// 피드백) 세그먼트로 승격 — 세그먼트를 처음 고르는 순간에만 API 호출(overview?includeCharges=1)
// 하고, 다른 세그먼트로 갔다 와도 받아둔 데이터·필터를 유지한다(hidden 처리).
//
// 실결제(payment)와 수기 지급(manual)을 명확히 구분한다(2026-07-18 피드백) — 수기 지급은
// 실제 매출이 아니라서 섞어 보면 매출을 오독하게 됨. 결제액보다 지급 NC가 많은 결제엔
// "이벤트" 배지(×2 프로모 등) — 이벤트 기간에 결제가 얼마나 늘었는지 배지로 훑어볼 수 있다.
const KIND_OPTIONS = [
  { id: "all",     label: "전체" },
  { id: "payment", label: "실결제" },
  { id: "manual",  label: "수기 지급" },
];

export default function ChargeHistory({ range: initialRange, active }) {
  // 세그먼트에 한 번이라도 들어왔을 때만 조회 시작 — 탭만 열고 추이만 보는 사람에게
  // 불필요한 호출을 안 한다(기존 "펼칠 때 조회" 동작 유지).
  const [visited, setVisited] = useState(false);
  // 기간은 탭과 독립(2026-07-18 피드백 — 표마다 독립적으로 조작 가능해야) — 탭 범위를
  // 초기값으로만 쓰고, 여기서 바꾼 기간은 이 표에만 적용된다.
  const [range, setRange] = useState(initialRange);
  const [charges, setCharges] = useState(null); // null = 아직 미조회
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [kind, setKind] = useState("all");
  const [method, setMethod] = useState("");

  const fetchCharges = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 3분 캐시 — 같은 기간을 다시 열면 즉시(2026-07-18 속도 피드백).
      const res = await cachedCall(
        `charges|${range.from}|${range.to}`,
        () => analyticsApi.overview({ from: range.from, to: range.to, includeCharges: 1 })
      );
      setCharges(res.charges || []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => {
    if (active) setVisited(true);
  }, [active]);

  useEffect(() => {
    if (visited) fetchCharges();
  }, [visited, fetchCharges]);

  const all = charges || [];
  const methodOptions = [...new Set(all.filter((c) => c.kind === "payment" && c.method).map((c) => c.method))];
  const filtered = all
    .filter((c) => kind === "all" || c.kind === kind)
    .filter((c) => !method || c.method === method);

  // 요약 — 수기 지급을 매출에 절대 섞지 않는다.
  const payments = all.filter((c) => c.kind === "payment" && !c.refunded);
  const revenueKrw = payments.reduce((s, c) => s + (c.amountKrw || 0), 0);
  const promoCount = payments.filter((c) => c.promo).length;
  const manualNc = all.filter((c) => c.kind === "manual").reduce((s, c) => s + (c.amountNc || 0), 0);
  const manualCount = all.filter((c) => c.kind === "manual").length;

  // 충전 KPI(2026-07-18) — "첫 결제는 호기심, 재충전은 만족의 증거". 전부 이미 받아온
  // 목록에서 계산(백엔드 변경 없음). 재충전자는 이 기간 안에서 2회 이상 결제한 사람 기준.
  const payerIds = new Set(payments.map((c) => c.userId));
  const perPayer = new Map();
  payments.forEach((c) => perPayer.set(c.userId, (perPayer.get(c.userId) || 0) + 1));
  const repeatPayers = [...perPayer.values()].filter((n) => n >= 2).length;
  const amountCounts = new Map();
  payments.forEach((c) => amountCounts.set(c.amountKrw || 0, (amountCounts.get(c.amountKrw || 0) || 0) + 1));
  const popular = [...amountCounts.entries()].sort((a, b) => b[1] - a[1])[0] || null;
  const avgKrw = payments.length ? Math.round(revenueKrw / payments.length) : 0;

  // 복사/CSV용 — 화면의 필터(구분·수단·기간)가 걸린 상태 그대로 내보낸다.
  const exportRows = [
    ["일시(KST)", "구분", "이메일", "결제 금액(KRW)", "지급 크레딧", "수단/사유", "환불"],
    ...filtered.map((c) => [
      fmt.kstDateTime(c.createdAt).slice(0, 16),
      c.kind === "payment" ? "실결제" : (c.amountNc < 0 ? "수기 회수" : "수기 지급"),
      c.email || c.userId,
      c.kind === "payment" ? (c.amountKrw || 0) : "",
      c.amountNc,
      c.kind === "payment" ? (c.method || "") : (c.reason || ""),
      c.refunded ? (c.refundedAt ? fmt.kstDateTime(c.refundedAt).slice(0, 16) : "환불됨") : "",
    ]),
  ];

  return (
    <div className={active ? "animate-fade-in" : "hidden"}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
          충전 내역 <span className="font-normal normal-case text-ink-400">— 결제·환불·수기 지급 원장 · 최신순</span>
        </h3>
        {/* 컨트롤 순서는 전 섹션 공통 규칙(2026-07-18): 필터 → 기간 → 집계 → 복사 → CSV */}
        <div className="flex flex-wrap items-center gap-2">
          <ToggleGroup options={KIND_OPTIONS} value={kind} onChange={setKind} />
          <MiniSelect
            value={method}
            onChange={setMethod}
            options={[{ value: "", label: "수단: 전체" }, ...methodOptions.map((m) => ({ value: m, label: m }))]}
          />
          <SectionRangePicker from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />
          <CopyButton rows={exportRows} />
          <ExportButton filename="charges" rows={exportRows} />
        </div>
      </div>

      {charges !== null && (
        <div className="mb-3 grid grid-cols-2 gap-2 lg:grid-cols-4">
          <MiniStat label="결제자" value={`${fmt.int(payerIds.size)}명`} sub={`실결제 ${fmt.int(payments.length)}건 · ${fmt.krw(revenueKrw)}`} />
          <MiniStat label="재충전자 (2회 이상)" value={`${fmt.int(repeatPayers)}명`} sub="기간 내 2회 이상 결제" />
          <MiniStat
            label="가장 많이 선택한 금액"
            value={popular ? fmt.krw(popular[0]) : "—"}
            sub={popular ? `${fmt.int(popular[1])}건 선택` : "이 기간 결제 없음"}
          />
          <MiniStat label="평균 결제액" value={payments.length ? fmt.krw(avgKrw) : "—"} sub="환불 제외 기준" />
        </div>
      )}

      {charges !== null && (promoCount > 0 || manualCount > 0) && (
        <p className="mb-3 rounded-lg bg-ink-50 px-3 py-2 text-xs text-ink-600">
          {promoCount > 0 && <>이벤트(×2) 적용 결제 <b className="tabular-nums">{fmt.int(promoCount)}건</b></>}
          {promoCount > 0 && manualCount > 0 && <> · </>}
          {manualCount > 0 && (
            <>수기 지급 <b className="tabular-nums">{fmt.int(manualCount)}건 · {fmt.nc(manualNc)} 크레딧</b>{" "}
            <span className="text-amber-700">— 매출·결제 통계 불포함</span></>
          )}
        </p>
      )}

      {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {!error && loading && charges === null && <div className="text-sm text-ink-400">불러오는 중…</div>}
      {!error && charges !== null && filtered.length === 0 && (
        <div className="rounded-lg border border-ink-100 p-6 text-center text-sm text-ink-400">
          조건에 맞는 내역이 없습니다.
        </div>
      )}
      {!error && charges !== null && filtered.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-lg border border-ink-100">
            <table className="min-w-full text-sm">
              <thead className="bg-ink-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-ink-500">일시 (KST)</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-ink-500">구분</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-ink-500">이메일</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-ink-500">결제 금액</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-ink-500">지급 크레딧</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-ink-500">수단 / 사유</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-ink-500">환불</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {filtered.map((c, i) => (
                  <tr key={`${c.userId}-${c.createdAt}-${i}`} className={`hover:bg-ink-50 ${c.refunded ? "opacity-60" : ""}`}>
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums text-ink-600">{fmt.kstDateTime(c.createdAt).slice(0, 16)}</td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {c.kind === "payment" ? (
                        <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700">실결제</span>
                      ) : (
                        <span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                          {c.amountNc < 0 ? "수기 회수" : "수기 지급"}
                        </span>
                      )}
                    </td>
                    <td className="max-w-[14rem] truncate px-3 py-2 text-ink-700">{c.email || c.userId}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-medium text-ink-900">
                      {c.kind === "payment" ? fmt.krw(c.amountKrw) : <span className="text-ink-300">—</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-ink-500">
                      {fmt.nc(c.amountNc)}
                      {c.promo && (
                        <span className="ml-1 rounded bg-brand-50 px-1 py-0.5 text-[10px] font-medium text-brand-700">이벤트</span>
                      )}
                    </td>
                    <td className="max-w-[14rem] truncate whitespace-nowrap px-3 py-2 text-ink-500">
                      {c.kind === "payment" ? (c.method || "—") : (c.reason || "—")}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {c.refunded ? (
                        <span className="rounded bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-600">
                          {c.refundedAt ? fmt.kstDateTime(c.refundedAt).slice(0, 16) : "환불됨"}
                        </span>
                      ) : (
                        <span className="text-xs text-ink-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-ink-400">
            수기 지급은 매출에 포함되지 않습니다. "이벤트" 배지 = 결제액보다 많은 크레딧을
            받은 결제(×2 프로모 등).
          </p>
        </>
      )}
    </div>
  );
}

function MiniStat({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-ink-200 bg-white p-2.5">
      <div className="text-[11px] text-ink-500">{label}</div>
      <div className="mt-0.5 text-sm font-semibold tabular-nums text-ink-900">{value}</div>
      {sub && <div className="mt-0.5 truncate text-[11px] tabular-nums text-ink-400">{sub}</div>}
    </div>
  );
}
